import axios from 'axios'

// Dynamic API base URL that works for both localhost and network access
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return '/api'
  }
  
  // In development, check if we're accessing from localhost or network
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3001/api'
  } else {
    // Use the same host but port 3001 for network access
    return `http://${hostname}:3001/api`
  }
}

const API_BASE_URL = getApiBaseUrl()

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem('token')) {
      // Only clear session and redirect if the user thought they were logged in
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData)
}

// Photos API
export const photosAPI = {
  upload: (formData) => {
    return api.post('/photos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 600000 // 10 minutes for large image processing
    })
  },

  uploadToMarker: (formData) => {
    return api.post('/photos/upload-to-marker', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 600000 // 10 minutes for large image processing
    })
  },

  getAll: () => api.get('/photos'),
  getByMarkerGroup: (markerGroupId) => api.get(`/photos/marker-group/${markerGroupId}`),
  search: (tags) => api.get(`/photos/search?tags=${encodeURIComponent(tags)}`),
  searchTags: (query) => api.get(`/photos/tags/search?q=${encodeURIComponent(query)}`),
  updateTags: (photoId, tags) => api.put(`/photos/${photoId}/tags`, { tags }),
  updateMarkerGroup: (photoId, markerGroupId) => api.put(`/photos/${photoId}/marker-group`, { marker_group_id: markerGroupId }),
  delete: (photoId) => api.delete(`/photos/${photoId}`),
  download: (photoId) => {
    return api.get(`/photos/download/${photoId}`, {
      responseType: 'blob'
    })
  },
  getDownloadUrl: (photoId) => `${API_BASE_URL}/photos/download/${photoId}`,
  getImage: (filename) => `${API_BASE_URL}/photos/image/${filename}`
}

export default api 